import React, { useState } from "react"
import { Link } from "gatsby"
import ReactTooltip from "react-tooltip"
import { InformationCircleIcon } from "@heroicons/react/outline"

import { readableNumber } from "../../utils/text"
import { topicSeoDate } from "../../utils/topics"
import {
  getThemes,
  getTopics,
  getFlair,
  getProducts,
} from "../../utils/gummysearchSummary"

const TOP_COUNT = 5
const SNIPPET_COUNT = 3
const BAR_COLOR = "250, 204, 21"

const barPercent = (count, max, min = 8) => {
  if (!count) return 4
  return Math.max(min, Math.round((count / max) * 100))
}

const barOpacity = (count, max) => {
  if (!count || !max) return 0
  return (count / max) * 0.65
}

const snippetLine = snippets =>
  (snippets || [])
    .filter(Boolean)
    .slice(0, SNIPPET_COUNT)
    .join(" · ")

const formatTimelineLabel = (label, { monthShort = false } = {}) => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
    return new Date(`${label}T12:00:00`).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })
  }
  if (/^\d{4}-\d{2}$/.test(label)) {
    const month = new Date(`${label}-01T12:00:00`).toLocaleDateString("en-US", {
      month: "short",
    })
    return monthShort ? month.slice(0, 3) : month
  }
  return label
}

const parseMemberTimeline = (series, options = {}) => {
  if (!Array.isArray(series)) return []
  return series
    .filter(([, count]) => count != null && count > 0)
    .map(([label, count]) => ({
      key: label,
      label: formatTimelineLabel(label, options),
      count: Number(count),
    }))
}

const formatTimelineTooltipLabel = key => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
    return new Date(`${key}T12:00:00`).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    })
  }
  if (/^\d{4}-\d{2}$/.test(key)) {
    return new Date(`${key}-01T12:00:00`).toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    })
  }
  return key
}

const toNewTimelineItems = items =>
  items.slice(1).map((item, i) => ({
    ...item,
    cumulative: item.count,
    count: Math.max(0, item.count - items[i].count),
  }))

const GrowthTimelineToggle = ({ mode, onChange }) => (
  <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5 text-xs">
    <button
      type="button"
      onClick={() => onChange("new")}
      className={
        mode === "new"
          ? "rounded bg-white px-2.5 py-1 font-medium text-amber-900 shadow-sm"
          : "rounded px-2.5 py-1 text-gray-600 hover:text-gray-900"
      }
    >
      New
    </button>
    <button
      type="button"
      onClick={() => onChange("cumulative")}
      className={
        mode === "cumulative"
          ? "rounded bg-white px-2.5 py-1 font-medium text-amber-900 shadow-sm"
          : "rounded px-2.5 py-1 text-gray-600 hover:text-gray-900"
      }
    >
      Cumulative
    </button>
  </div>
)

const MemberGrowthChart = ({
  title,
  items,
  ariaLabel,
  mode = "cumulative",
  scaleFromZero = false,
}) => {
  if (items.length < 2) return null

  const displayItems =
    mode === "new"
      ? toNewTimelineItems(items)
      : items.map(i => ({ ...i, cumulative: i.count }))

  const counts = displayItems.map(item => item.count)
  const min = Math.min(...counts)
  const max = Math.max(...counts)
  const range = max - min || 1
  const maxHeight = 64
  const useZeroScale = scaleFromZero || mode === "new"

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-900 mb-2">{title}</h4>
      <ul className="sr-only">
        {displayItems.map(item => (
          <li key={item.key}>
            {item.label}: {readableNumber(item.count)} members
          </li>
        ))}
      </ul>
      <div
        className="w-full overflow-visible"
        role="img"
        aria-label={ariaLabel}
      >
        <div className="flex w-full items-end gap-px pt-8">
          {displayItems.map(item => {
            const height = useZeroScale
              ? Math.max(4, Math.round((item.count / max) * maxHeight))
              : Math.max(
                  4,
                  Math.round(((item.count - min) / range) * maxHeight)
                )
            const barOpacityValue =
              barOpacity(
                useZeroScale ? item.count : item.count - min,
                useZeroScale ? max : range
              ) || 0.35

            return (
              <div
                key={item.key}
                className="group relative flex min-w-0 flex-1 flex-col items-center"
              >
                <div className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                  <div className="whitespace-nowrap rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs shadow-lg">
                    <div className="font-medium text-gray-900">
                      {formatTimelineTooltipLabel(item.key)}
                    </div>
                    {mode === "new" ? (
                      <div className="mt-0.5 font-semibold tabular-nums text-amber-600">
                        +{readableNumber(item.count)} new
                      </div>
                    ) : (
                      <div className="mt-0.5 font-semibold tabular-nums text-amber-600">
                        {readableNumber(item.count)} members
                      </div>
                    )}
                  </div>
                  <div className="mx-auto h-0 w-0 border-x-[5px] border-t-[5px] border-x-transparent border-t-gray-200" />
                </div>
                <div
                  className="w-full max-w-full cursor-default rounded-t transition-all duration-150 group-hover:brightness-110 group-hover:shadow-md"
                  style={{
                    height: `${height}px`,
                    backgroundColor: `rgba(${BAR_COLOR}, ${barOpacityValue})`,
                  }}
                />
                <span className="mt-1 w-full truncate text-center text-[9px] leading-none text-gray-400">
                  {item.label}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const TOOLTIP_POST_COUNT = 3

const AnalysisBarTooltip = ({ item }) => {
  const titles = (item.snippets || [])
    .filter(Boolean)
    .slice(0, TOOLTIP_POST_COUNT)
  const postWord = item.count === 1 ? "post" : "posts"

  return (
    <div className="pointer-events-none absolute bottom-full left-3 z-30 mb-2 w-[min(100%,17rem)] opacity-0 transition-opacity duration-150 group-hover:opacity-100">
      <div className="rounded-md border border-gray-200 bg-white px-2.5 py-2 text-xs shadow-lg">
        <div className="font-medium text-gray-900">{item.label}</div>
        <div className="mt-0.5 font-semibold tabular-nums text-amber-600">
          {item.count} {postWord} in the past month
        </div>
        {titles.length ? (
          <ol className="mt-2 space-y-1.5 border-t border-gray-100 pt-2 pl-3.5 text-gray-600 list-decimal">
            {titles.map((title, idx) => (
              <li key={idx} className="leading-snug pl-0.5">
                {title}
              </li>
            ))}
          </ol>
        ) : null}
      </div>
      <div className="ml-5 h-0 w-0 border-x-[5px] border-t-[5px] border-x-transparent border-t-gray-200" />
    </div>
  )
}

const AnalysisBarList = ({ items }) => {
  const max = Math.max(...items.map(item => item.count), 1)

  return (
    <ul className="flex flex-col gap-1 pt-1">
      {items.map((item, i) => {
        const line = snippetLine(item.snippets)
        return (
          <li key={item.label || item.slug} className="group relative">
            <AnalysisBarTooltip item={item} />
            <div className="relative flex w-full min-w-0 items-stretch overflow-hidden rounded-md bg-gray-50 text-sm transition-all duration-150 group-hover:brightness-[1.02]">
              <div
                className="absolute inset-y-0 left-0 rounded-md transition-all duration-150 group-hover:brightness-110"
                style={{
                  width: `${barPercent(item.count, max)}%`,
                  backgroundColor: `rgba(${BAR_COLOR}, ${barOpacity(
                    item.count,
                    max
                  )})`,
                }}
                aria-hidden="true"
              />
              <div className="relative z-10 flex min-w-0 flex-1 items-center justify-between gap-2 px-2 py-1.5">
                <div className="flex min-w-0 flex-1 gap-2">
                  <span className="w-6 shrink-0 pt-0.5 text-xs tabular-nums text-gray-400">
                    #{i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium text-gray-800">
                      {item.label}
                    </div>
                    {line ? (
                      <div className="truncate text-[11px] leading-tight text-gray-500">
                        {line}
                      </div>
                    ) : null}
                  </div>
                </div>
                <span className="shrink-0 self-center pl-2 tabular-nums font-semibold text-gray-700">
                  {item.count}
                </span>
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

const AnalysisSection = ({ title, items }) => (
  <div className="min-w-0">
    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
      {title}
    </h3>
    <AnalysisBarList items={items} />
  </div>
)

const GummySearchAnalysis = ({ gummysearch, communityName }) => {
  const [timelineMode, setTimelineMode] = useState("new")

  if (!gummysearch) return null

  const calculated = gummysearch.calculated || {}
  const posts = gummysearch.results_submissions_top
  const subredditName = (gummysearch.data?.name || communityName || "").replace(
    /^r\//i,
    ""
  )
  const gummyUrl = `https://gummysearch.com/r/${subredditName}/?ref=thehiveindex.com&utm_source=thehiveindex.com`
  const syncedLabel = topicSeoDate(gummysearch.results_updated_at)

  const yearlyGrowth = calculated.growth_members_yearly
  const yearlyMembers = calculated.per_year_members
  const allTimeline = parseMemberTimeline(calculated.timelines?.all)
  const yearTimeline = parseMemberTimeline(calculated.timelines?.year, {
    monthShort: true,
  })
  const themes = getThemes(posts, gummysearch.results_analysis, TOP_COUNT)
  const topics = getTopics(posts, gummysearch.results_analysis, TOP_COUNT)
  const flair = getFlair(posts, TOP_COUNT)
  const products = getProducts(gummysearch, posts, TOP_COUNT)

  const sections = [
    themes.length ? { key: "themes", title: "Themes", items: themes } : null,
    topics.length ? { key: "topics", title: "Topics", items: topics } : null,
    flair.length ? { key: "flair", title: "Flair", items: flair } : null,
    products.length
      ? { key: "products", title: "Product recommendations", items: products }
      : null,
  ].filter(Boolean)

  const hasGrowthStats = yearlyGrowth != null || yearlyMembers != null
  const hasGrowthCharts = allTimeline.length >= 2 || yearTimeline.length >= 2
  const hasGrowth = hasGrowthStats || hasGrowthCharts
  if (!sections.length && !hasGrowth) return null

  return (
    <section aria-labelledby="gummysearch-analysis-title">
      <div className="rounded-lg bg-white overflow-visible shadow p-6">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-4">
          <h2
            className="text-base font-medium text-gray-900 mb-0"
            id="gummysearch-analysis-title"
          >
            Subreddit Analysis
          </h2>
          <Link
            to="/gummysearch-integration/"
            className="text-xs text-gray-500 underline hover:text-gray-700"
          >
            via GummySearch
          </Link>
          <div
            data-for="gummysearch-integration"
            data-tip="Stats and discussion analysis for Reddit communities come from GummySearch. Learn more on our integration page."
          >
            <InformationCircleIcon
              className="h-4 w-4 text-gray-400 cursor-pointer"
              aria-hidden="true"
            />
          </div>
          <ReactTooltip
            id="gummysearch-integration"
            effect="solid"
            backgroundColor="black"
            textColor="white"
            className="max-w-xs text-xs"
          />
        </div>

        {hasGrowth ? (
          <div className="mb-4 space-y-4">
            {hasGrowthStats ? (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                {yearlyMembers != null ? (
                  <div>
                    <span className="text-gray-500">Yearly: </span>
                    <span className="font-semibold text-amber-600">
                      +{readableNumber(yearlyMembers)} members
                    </span>
                  </div>
                ) : null}
                {yearlyGrowth != null ? (
                  <div>
                    <span className="text-gray-500">Growth: </span>
                    <span className="font-semibold text-amber-600">
                      +{Number(yearlyGrowth).toFixed(1)}% / year
                    </span>
                  </div>
                ) : null}
              </div>
            ) : null}
            {hasGrowthCharts ? (
              <div className="space-y-4 pt-1 border-t border-gray-100">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-0">
                    Member growth over time
                  </h3>
                  <GrowthTimelineToggle
                    mode={timelineMode}
                    onChange={setTimelineMode}
                  />
                </div>
                <div className="space-y-6">
                  {allTimeline.length >= 2 ? (
                    <MemberGrowthChart
                      title="All time (yearly)"
                      items={allTimeline}
                      mode={timelineMode}
                      ariaLabel="Member count by year, all time"
                      scaleFromZero
                    />
                  ) : null}
                  {yearTimeline.length >= 2 ? (
                    <MemberGrowthChart
                      title="Past year (monthly)"
                      items={yearTimeline}
                      mode={timelineMode}
                      ariaLabel="Member count by month over the past year"
                    />
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {sections.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {sections.map(section => (
              <AnalysisSection
                key={section.key}
                title={section.title}
                items={section.items}
              />
            ))}
          </div>
        ) : null}

        <div className="text-xs text-gray-400 mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-center gap-x-3 gap-y-1">
          <a
            href={gummyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-gray-600"
          >
            View full analysis on GummySearch
          </a>
          {syncedLabel ? <span>Synced {syncedLabel}</span> : null}
        </div>
      </div>
    </section>
  )
}

export default GummySearchAnalysis
